/**
 * Wires up the Cart Items widget's quantity stepper (+/-) and Remove
 * controls (gwschild/inc/elementor-widgets/class-gws-elementor-widget-cart-items.php).
 * A close port of the product card's own stepper logic
 * (src/js/elementor-product-card-quantity.js), reusing the exact same
 * /gws/v1/cart/quantity REST route (product_id-keyed, increment/decrement,
 * removes at zero — nothing cart-items-specific needed server-side) and
 * window.gwsCart settings (restUrl/nonce, printed in wp_footer regardless
 * of which cart widgets are on the page).
 *
 * Differs from the product card's version in one structural way: a cart
 * row is ALREADY a stepper from the moment it's server-rendered (never a
 * "+" button to transform), and after every click the ENTIRE
 * `.gws-cart-items`/`.gws-cart-summary` content gets replaced wholesale
 * via the fragments the REST response returns — GWS_Cart_Panel_Fragments()
 * (gwschild/inc/woocommerce.php) re-renders the full list/summary server-
 * side on every change, the same source of truth the initial page load
 * used, so there's no risk of client-side stepper math drifting from
 * what WooCommerce's cart API actually did (e.g. hitting a stock limit).
 * This means click handlers MUST be event-delegated from the widget's own
 * outer `.gws-cart-items-widget` wrapper (which a fragment swap never
 * touches) rather than bound directly to row buttons, which get destroyed
 * and recreated by every fragment swap.
 */
( function () {
	'use strict';

	// product_id (string) => true while a request for that product is
	// in flight, so a rapid double-click doesn't fire two overlapping
	// requests — guards by product_id rather than by DOM element, since
	// the clicked element itself may not exist anymore once the fragment
	// swap that resolves this same click completes.
	var busyProductIds = {};

	function cartSettings() {
		return window.gwsCart || null;
	}

	function applyFragments( fragments ) {
		if ( ! fragments ) {
			return;
		}

		Object.keys( fragments ).forEach( function ( selector ) {
			document.querySelectorAll( selector ).forEach( function ( el ) {
				el.outerHTML = fragments[ selector ];
			} );
		} );
	}

	/**
	 * Tells any WooCommerce Blocks cart-aware component on the page (Mini
	 * Cart, Cart, Checkout, All Products) to refetch from the Store API —
	 * this route mutates the cart entirely outside both the classic
	 * wc-ajax add-to-cart flow and the Store API, so neither of WooCommerce
	 * Blocks' own two refresh triggers fires on its own. Same as the
	 * product card's own stepper.
	 */
	function notifyBlocksCartChanged() {
		if ( ! window.CustomEvent ) {
			return;
		}

		document.body.dispatchEvent(
			new CustomEvent( 'wc-blocks_added_to_cart', {
				bubbles: true,
				cancelable: true,
				detail: { preserveCartData: false },
			} )
		);
	}

	/**
	 * Tells any product card on the page showing this same product to
	 * reconcile its own quantity display (elementor-product-card-quantity.js
	 * listens for this and re-syncs from /gws/v1/cart) — a stepper click in
	 * here already refreshes THIS widget's own markup via fragments, but a
	 * product card elsewhere on the page has no other way to learn its
	 * quantity just changed.
	 */
	function notifyGwsCartUpdated() {
		if ( ! window.CustomEvent ) {
			return;
		}

		document.dispatchEvent( new CustomEvent( 'gws-cart-updated' ) );
	}

	function postQuantity( productId, action ) {
		var config = cartSettings();
		if ( ! config ) {
			return Promise.resolve( null );
		}

		return fetch( config.restUrl + 'cart/quantity', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-WP-Nonce': config.nonce,
			},
			body: JSON.stringify( { product_id: productId, action: action } ),
		} )
			.then( function ( response ) { return response.ok ? response.json() : null; } )
			.catch( function () { return null; } );
	}

	function handleClick( productId, action ) {
		if ( busyProductIds[ productId ] ) {
			return;
		}

		busyProductIds[ productId ] = true;

		postQuantity( productId, action ).then( function ( result ) {
			delete busyProductIds[ productId ];

			if ( ! result ) {
				return;
			}

			// Replaces .gws-cart-items/.gws-cart-summary/.cart-count
			// wholesale with fresh server-rendered markup — no manual
			// DOM patching of the clicked row needed.
			applyFragments( result.fragments );
			notifyBlocksCartChanged();
			notifyGwsCartUpdated();
		} );
	}

	function onWidgetClick( event ) {
		var minus = event.target.closest( '.gws-cart-item__qty-minus' );
		var plus = event.target.closest( '.gws-cart-item__qty-plus' );
		var remove = event.target.closest( '.gws-cart-item__remove' );

		var control = minus || plus || remove;

		if ( ! control ) {
			return;
		}

		var productId = control.getAttribute( 'data-product_id' )
			|| ( control.closest( '[data-product_id]' ) && control.closest( '[data-product_id]' ).getAttribute( 'data-product_id' ) );

		if ( ! productId ) {
			return;
		}

		event.preventDefault();
		handleClick( productId, plus ? 'increment' : 'decrement' );
	}

	function registerElementReadyHook() {
		window.elementorFrontend.hooks.addAction( 'frontend/element_ready/gws-cart-items.default', function ( $scope ) {
			var widget = $scope[ 0 ].querySelector( '.gws-cart-items-widget' );

			if ( ! widget ) {
				return;
			}

			widget.addEventListener( 'click', onWidgetClick );
		} );
	}

	if ( window.elementorFrontend && window.elementorFrontend.hooks ) {
		registerElementReadyHook();
	} else {
		window.addEventListener( 'elementor/frontend/init', registerElementReadyHook );
	}
} )();
